/**
 * Typed client for the admin API.
 *
 * Authentication rides on an HttpOnly session cookie the page cannot read. Every
 * write must also echo the CSRF token from the second, readable cookie — that
 * double submit is what stops another site driving these endpoints.
 *
 * The transport itself lives in ./httpClient, shared with the member-account
 * client; only the prefix and the cookie name differ between the two.
 */

import { ApiError, requestJson, type RequestOptions } from "./httpClient";
import type { Club } from "./api";

export const CSRF_COOKIE = "tau_csrf";

/**
 * The admin call sites read as `AdminError`, but it is the same class the rest
 * of the app throws — a subclass here would only mean a catch/re-throw on every
 * request that discarded the original stack for a `name` string nobody reads.
 */
export { ApiError as AdminError };

export interface AdminSession {
  readonly username: string;
  readonly expires_at: string;
  readonly csrf_token: string;
}

export interface AdminMember {
  readonly username: string;
  readonly display_name: string | null;
  readonly role: string | null;
  readonly live: boolean;
}

export interface Activity {
  readonly id: string;
  readonly title: string;
  readonly date: string;
  readonly start_time: string;
  readonly detail: string;
  readonly location_note: string;
}

export type ActivityDraft = Omit<Activity, "id">;

export interface SavedResult {
  readonly saved: boolean;
  readonly message: string;
}

const request = <T,>(path: string, options?: RequestOptions) =>
  requestJson<T>("/api/admin", path, options, CSRF_COOKIE);

export interface Assignment {
  readonly roster_username: string;
  readonly display_name: string;
  /** The account writing this player's profile, or null if nobody is. */
  readonly account: string | null;
}

export interface Assignments {
  readonly assignments: readonly Assignment[];
  readonly unassigned_accounts: readonly string[];
  /**
   * Profiles whose roster entry has since been deleted. They have no row in
   * `assignments` — that list is built by walking the roster — so without
   * surfacing them here there is no way to clear one.
   */
  readonly stranded: readonly Assignment[];
}

export interface AdminPuzzle {
  readonly id: number;
  readonly title: string;
  readonly creator: string;
  readonly archive_difficulty: number | null;
  readonly puzzle_url: string;
  readonly solution_url: string;
  readonly set_name: string;
  readonly added_on: string | null;
  readonly is_published: boolean;
  /** How much community rating a delete would take with it. */
  readonly rating_count: number;
}

export interface AdminPuzzles {
  readonly puzzles: readonly AdminPuzzle[];
  readonly next_id: number;
  readonly sets: readonly string[];
}

export type PuzzleDraft = Omit<AdminPuzzle, "id" | "rating_count">;

export const adminApi = {
  status: () => request<{ configured: boolean }>("/status"),
  session: () => request<AdminSession>("/session"),
  login: (username: string, password: string) =>
    request<AdminSession>("/login", { method: "POST", body: { username, password } }),
  logout: () => request<SavedResult>("/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<SavedResult>("/password", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    }),

  club: () => request<Club>("/club"),
  saveClub: (club: Club) => request<Club>("/club", { method: "PUT", body: club }),

  roster: () => request<{ members: AdminMember[]; stats_checked: boolean }>("/roster"),
  saveRoster: (members: readonly Partial<AdminMember>[]) =>
    request<SavedResult>("/roster", { method: "PUT", body: { members } }),
  refreshRoster: () => request<SavedResult>("/roster/refresh", { method: "POST" }),

  activities: () => request<{ activities: Activity[] }>("/activities"),
  addActivity: (draft: ActivityDraft) =>
    request<Activity>("/activities", { method: "POST", body: draft }),
  updateActivity: (id: string, draft: ActivityDraft) =>
    request<Activity>(`/activities/${encodeURIComponent(id)}`, { method: "PUT", body: draft }),
  assignments: () => request<Assignments>("/assignments"),
  assign: (rosterUsername: string, account: string | null) =>
    request<SavedResult>("/assignments", {
      method: "PUT",
      body: { roster_username: rosterUsername, account },
    }),

  puzzles: () => request<AdminPuzzles>("/puzzles"),
  addPuzzle: (draft: PuzzleDraft) =>
    request<AdminPuzzle>("/puzzles", { method: "POST", body: draft }),
  updatePuzzle: (id: number, draft: PuzzleDraft) =>
    request<AdminPuzzle>(`/puzzles/${id}`, { method: "PUT", body: draft }),
  deletePuzzle: (id: number) =>
    request<SavedResult>(`/puzzles/${id}`, { method: "DELETE" }),

  deleteActivity: (id: string) =>
    request<SavedResult>(`/activities/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
